import { Outlet, useLocation } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import { Breakpoints, useWindowSize } from '@urbanx/agx-ui-components';
import { useFirstPaintLoader } from '@urbanx/agx.ui-components2';
import SideBar from 'components/sidebar/Sidebar';
import { useEffect, useState } from 'react';
import MobileHeader from 'components/mobileHeader/MobileHeader';
import { ContentScrollContext } from 'contexts';
import { useAutomationFailureNotifier } from 'pages/automation-errors/useAutomationFailureNotifier';
import { NavigationHistoryProvider } from 'hooks/useNavigationHistory';

const INITIAL_FETCH_DETECTION_DELAY_MS = 100;

const MainLayout = () => {
  useAutomationFailureNotifier();
  const activeFetchCount = useIsFetching();
  const screenSize = useWindowSize();
  const isMobile = screenSize === Breakpoints.Mobile;
  const isTabletPortrait = screenSize === Breakpoints.Tablet_Portrait;
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(true);
  const [contentScrollPosition, setContentScrollPosition] = useState(0);
  const [isReadyToObserveFetches, setIsReadyToObserveFetches] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setIsReadyToObserveFetches(true);
    }, INITIAL_FETCH_DETECTION_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    setContentScrollPosition(0);
  }, [location.pathname]);

  useFirstPaintLoader({
    isAppReady: isReadyToObserveFetches,
    isInitialLoadComplete: activeFetchCount === 0,
  });

  return (
    <div className="container">
      <SideBar isMenuCollapsed={isMenuCollapsed} setIsMenuCollapsed={setIsMenuCollapsed} />
      {isTabletPortrait && (
        <div className="backdrop" onClick={() => setIsMenuCollapsed(true)}></div>
      )}
      <ContentScrollContext.Provider value={{ contentScrollPosition, setContentScrollPosition }}>
        <div
          className="main-content"
          id="main-content"
          onScroll={(e: React.UIEvent<HTMLDivElement>) => {
            const newScrollPosition = e.currentTarget.scrollTop;
            setContentScrollPosition(newScrollPosition);
          }}
        >
          {(isMobile || isTabletPortrait) && (
            <MobileHeader
              isMenuCollapsed={isMenuCollapsed}
              setIsMenuCollapsed={setIsMenuCollapsed}
            />
          )}
          <NavigationHistoryProvider>
            <Outlet />
          </NavigationHistoryProvider>
        </div>
      </ContentScrollContext.Provider>
    </div>
  );
};

export default MainLayout;
